/**
 * The police in the traffic: what they look like where you are, how many
 * there are, how they sound responding, and who gets out of their way.
 * Pure: drawn by `three/police.ts`, driven by `three/traffic.ts`.
 */

import type { Country } from '../geo/country.js';

/**
 * How a livery is laid out on a car's painted body (see `three/police.ts`):
 * - `battenburg`: two rows of squares, `a` and `b`, along each side between
 *   the sills and the waistline, on a `base` body: the British, Swedish,
 *   Danish, Norwegian and Finnish pattern;
 * - `band`: a solid `a` band along each side on a `base` body, `b` above it
 *   as a pinstripe: Germany's blue on silver;
 * - `doors`: `b` doors and roof on an `a` body: the American black and white;
 * - `stripe`: a `base` body with an `a` stripe along each side: anywhere else.
 */
export type LiveryPattern = 'battenburg' | 'band' | 'doors' | 'stripe';

export interface Livery {
  pattern: LiveryPattern;
  base: number;
  a: number;
  b: number;
  /** How the siren sounds there. */
  siren: SirenKind;
  /** The officers' uniform: jacket, trousers and cap. */
  uniform: { jacket: number; trousers: number; cap: number };
}

const WHITE = 0xf2f2f0, BLUE = 0x1f4f9e, YELLOW = 0xe8e01a, NAVY = 0x16326b, SILVER = 0xc4c9cf, BLACK = 0x161718;

/** Each country's police livery: as near as a low-poly car shows them. */
export const LIVERIES: Record<Country | 'other', Livery> = {
  se: { pattern: 'battenburg', base: WHITE, a: BLUE, b: YELLOW, siren: 'twoTone', uniform: { jacket: 0x1d2a46, trousers: 0x1a2236, cap: 0x1d2a46 } },
  dk: { pattern: 'battenburg', base: WHITE, a: NAVY, b: YELLOW, siren: 'twoTone', uniform: { jacket: 0x1f2740, trousers: 0x1a1f30, cap: 0x1f2740 } },
  uk: { pattern: 'battenburg', base: WHITE, a: BLUE, b: 0xd6ea1c, siren: 'wail', uniform: { jacket: 0x17191c, trousers: 0x17191c, cap: 0x17191c } },
  no: { pattern: 'battenburg', base: WHITE, a: BLACK, b: YELLOW, siren: 'twoTone', uniform: { jacket: 0x1b1d22, trousers: 0x1b1d22, cap: 0x1b1d22 } },
  fi: { pattern: 'battenburg', base: WHITE, a: BLUE, b: WHITE, siren: 'twoTone', uniform: { jacket: 0x22304f, trousers: 0x1c2438, cap: 0x22304f } },
  de: { pattern: 'band', base: SILVER, a: BLUE, b: 0xeef0f2, siren: 'twoTone', uniform: { jacket: 0x2b4878, trousers: 0x22314d, cap: 0x2b4878 } },
  us: { pattern: 'doors', base: WHITE, a: BLACK, b: WHITE, siren: 'wail', uniform: { jacket: 0x1b2436, trousers: 0x1b2436, cap: 0x1b2436 } },
  other: { pattern: 'stripe', base: WHITE, a: BLUE, b: BLUE, siren: 'twoTone', uniform: { jacket: 0x22304a, trousers: 0x1c2436, cap: 0x22304a } },
};

export function liveryOf(country: Country | undefined): Livery {
  return LIVERIES[country ?? 'other'];
}

/**
 * The share of the traffic that is police, from how much road there is about
 * (`roads`, the traffic's own measure of how many cars the place calls
 * for): about one car in two hundred in the country, one in thirty-five in
 * a city.
 */
export function policeShare(roads: number): number {
  return 0.005 + 0.024 * Math.min(1, Math.max(0, roads / CITY_ROADS));
}
/** As many cars as a city's streets call for, near enough. */
const CITY_ROADS = 60;

/**
 * How a siren sounds: `twoTone`, the European hi-lo (the German
 * Martinshorn and its Nordic cousins: two notes a fourth apart, each held
 * about two thirds of a second), or `wail`, the American and British rise
 * and fall, a few seconds up and down.
 */
export type SirenKind = 'twoTone' | 'wail';

/** The siren's pitch `t` seconds after it starts, Hz, before any Doppler shift. */
export function sirenPitch(kind: SirenKind, t: number): number {
  if (kind === 'twoTone') return Math.floor(t / 0.65) % 2 === 0 ? 440 : 587;
  // Up from 600 to 1350 Hz in about 2.4 s, and down again as fast.
  const u = (t % 4.8) / 4.8;
  const rise = u < 0.5 ? u * 2 : (1 - u) * 2;
  return 600 + 750 * Math.sin((rise * Math.PI) / 2);
}

/** The Doppler factor for a source closing on a still listener at `closing` m/s: over 1 coming, under going. */
export function doppler(closing: number): number {
  return 343 / (343 - Math.max(-100, Math.min(100, closing)));
}

/** How far behind, and how near across, a responding car makes the one ahead give way, metres. */
export const YIELD_BEHIND = 70;
const YIELD_ACROSS = 4.5;

/**
 * Whether a car at (x, z) going (dx, dz) should give way to a responding
 * police car at (px, pz) going (pdx, pdz): the police coming up behind it on
 * its road, the same way, within `YIELD_BEHIND`.
 */
export function givesWay(x: number, z: number, dx: number, dz: number, px: number, pz: number, pdx: number, pdz: number): boolean {
  if (dx * pdx + dz * pdz < 0.6) return false;
  const rx = x - px, rz = z - pz;
  const ahead = rx * pdx + rz * pdz;
  if (ahead < 2 || ahead > YIELD_BEHIND) return false;
  return Math.abs(rx * pdz - rz * pdx) < YIELD_ACROSS;
}

/** How fast a car giving way with nowhere to go but the verge creeps along, m/s (about 20 km/h). */
export const YIELD_SPEED = 5.5;
/** How far over to the right it pulls, metres, and a responding car to the left to pass. */
export const PULL_OVER = 1.1;
/** How much faster than the road a responding car drives, as a share, and the most over, m/s. */
export const RESPONDING_SHARE = 1.3;
export const RESPONDING_OVER = 8;
